import React, { FC, Suspense } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { CustomErrorBoundary } from 'src/components/widgets/customErrorBoundary';
import { DropdownActionItem } from 'src/components/widgets/dropdown';
import { Spinner } from 'src/components/widgets/spinner';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import config from 'src/shared/config';
import { T, toLocalID } from 'src/shared/helpers';
import type { salesChannelSwitchQueriesCheckShopifyTestStoreQuery } from './__generated__/salesChannelSwitchQueriesCheckShopifyTestStoreQuery.graphql';
import { checkShopifyTestStoreQuery } from './salesChannelSwitchQueries';

interface SetAsMasterActionItemProps {
    salesChannelID: string;
    ecommerceName: string;
    isChecking: boolean;
    disabled: boolean;
    /** When set, the action is disabled whatever the store, and the tooltip explains why. */
    disabledReason?: string;
    onClick: () => void;
}

const DisabledItemWithReason: FC<{ reason: string }> = ({ reason }) => (
    <ZTooltip addContainer position='left' title={reason}>
        <DropdownActionItem disabled>{T._('Set as Master Integration', 'Admin')}</DropdownActionItem>
    </ZTooltip>
);

const PlainItem: FC<SetAsMasterActionItemProps> = ({ isChecking, disabled, onClick }) => (
    <DropdownActionItem disabled={disabled} onClick={onClick}>
        {isChecking ? <Spinner /> : T._('Set as Master Integration', 'Admin')}
    </DropdownActionItem>
);

/**
 * Shopify development and trial stores can't carry the real recurring charge a Master Integration needs. The backend
 * reports both as one flag (isTestStore), so both get the same disabled action and tooltip.
 */
const ShopifyItem: FC<SetAsMasterActionItemProps> = (props) => {
    const data = useLazyLoadQuery<salesChannelSwitchQueriesCheckShopifyTestStoreQuery>(
        checkShopifyTestStoreQuery,
        { salesChannelID: toLocalID(props.salesChannelID) },
        { fetchPolicy: 'store-or-network' }
    );

    if (!data.shopifyAuthEcommerceSettings?.isTestStore) return <PlainItem {...props} />;

    return (
        <DisabledItemWithReason
            reason={T._('Shopify development and trial stores cannot be set as the Master Integration. Use an active Shopify store instead.', 'Admin')}
        />
    );
};

/**
 * The "Set as Master Integration" row action. Menu items render only while the menu is open, so the Shopify test-store
 * check runs when the seller opens the menu, not for every row on page load. Outside production the check is skipped,
 * as the click-time check it replaces did: test environments run on development stores.
 */
const SetAsMasterActionItem: FC<SetAsMasterActionItemProps> = (props) => {
    if (props.disabledReason) return <DisabledItemWithReason reason={props.disabledReason} />;
    if (props.ecommerceName !== 'SHOPIFY' || !config.isProduction) return <PlainItem {...props} />;

    return (
        // If the check itself fails, the action stays available rather than blocking every Shopify store.
        <CustomErrorBoundary render={() => <PlainItem {...props} />}>
            <Suspense
                fallback={
                    <DropdownActionItem disabled>
                        <Spinner />
                    </DropdownActionItem>
                }
            >
                <ShopifyItem {...props} />
            </Suspense>
        </CustomErrorBoundary>
    );
};

export default SetAsMasterActionItem;
